/**
 * 通用 UI 工具层：Toast / Sheet / Sparkline / Empty / Skeleton / 格式化。
 * 对应原型 app.js 的同名能力，类名沿用 design-contract.md。
 */
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Icon } from './Icon';

/* ------------------------------ 格式化 ------------------------------ */

export const fmt = {
  num(v: unknown, decimals?: number): string {
    if (v === null || v === undefined || v === '') return '--';
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    return decimals == null ? String(Math.round(n)) : n.toFixed(decimals);
  },
  hhmm(ts: string | number): string {
    const d = new Date(ts);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${p(d.getHours())}:${p(d.getMinutes())}`;
  },
  dateTime(ts: string | number): string {
    const d = new Date(ts);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  },
  source(source: string): string {
    return ({ sim: '模拟设备', mqtt: 'MQTT', tuya: '涂鸦云', mihome: '米家' } as Record<string, string>)[source] ?? source;
  },
  /** 能力值 → 展示文本（枚举走 labels） */
  capText(cap: { type: string; labels?: Record<string, string> } | undefined, value: unknown): string {
    if (!cap) return String(value);
    if (cap.type === 'bool') return value ? '开' : '关';
    if (cap.type === 'enum' && cap.labels) return cap.labels[String(value)] ?? String(value);
    return fmt.num(value, cap.type === 'float' ? 1 : 0);
  },
};

/** 只读指标的中文名 */
export const READ_LABEL: Record<string, string> = {
  co2: 'CO₂',
  tvoc: 'TVOC',
  hcho: '甲醛',
  temp: '温度',
  humi: '湿度',
  press: '气压',
  bright: '亮度',
  power: '功率',
  gas: '燃气浓度',
};

/* ------------------------------- Toast ------------------------------ */

interface ToastItem {
  id: number;
  text: string;
  type: 'ok' | 'err';
}

let seq = 0;
let items: ToastItem[] = [];
const listeners = new Set<(list: ToastItem[]) => void>();

function show(text: string, type: 'ok' | 'err'): void {
  const id = ++seq;
  items = [...items, { id, text, type }];
  listeners.forEach((l) => l(items));
  setTimeout(() => {
    items = items.filter((i) => i.id !== id);
    listeners.forEach((l) => l(items));
  }, type === 'err' ? 3200 : 2200);
}

export const toast = {
  ok: (text: string) => show(text, 'ok'),
  err: (text: string) => show(text, 'err'),
};

/** 顶部轻提示宿主：在 App 根部挂一次 */
export function ToastHost() {
  const [list, setList] = useState<ToastItem[]>(items);
  useEffect(() => {
    listeners.add(setList);
    return () => void listeners.delete(setList);
  }, []);
  if (!list.length) return null;
  return (
    <div className="toast-host">
      {list.map((t) => (
        <div key={t.id} className={'toast' + (t.type === 'ok' ? ' toast--ok' : ' toast--err')}>
          <Icon name={t.type === 'err' ? 'alert-triangle' : 'circle-check'} size={16} />
          <span>{t.text}</span>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------- Sheet ------------------------------ */

export function Sheet({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className={'sheet' + (open ? ' is-open' : '')}>
      <div className="sheet__mask" onClick={onClose} />
      <div className="sheet__panel">
        <div className="sheet__grab" />
        <div className="sheet__head">
          <div className="sheet__title">{title}</div>
          <button className="btn btn--icon" onClick={onClose} aria-label="关闭">
            <Icon name="x" size={18} />
          </button>
        </div>
        {open && children}
      </div>
    </div>
  );
}

/* ----------------------------- Sparkline ---------------------------- */

export function Sparkline({ points, label }: { points: { t: string; v: number }[]; label?: string }) {
  const w = 300;
  const h = 44;
  const pad = 5;
  if (!points.length) return <div className="skeleton" style={{ height: h }} />;

  const vals = points.map((p) => Number(p.v));
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const span = max - min || 1;
  const step = (w - pad * 2) / (points.length - 1 || 1);
  const coords = points.map((p, i) => {
    const x = pad + i * step;
    const y = h - pad - ((Number(p.v) - min) / span) * (h - pad * 2);
    return [Number(x.toFixed(2)), Number(y.toFixed(2))];
  });
  const line = coords.map((c, i) => (i ? 'L' : 'M') + c[0] + ' ' + c[1]).join(' ');
  const area = `${line} L${coords[coords.length - 1][0]} ${h} L${coords[0][0]} ${h} Z`;
  const gid = `sparkFill-${label ?? 'x'}`.replace(/[^\w-]/g, '');

  return (
    <svg className="spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" role="img" aria-label={label ?? '趋势图'}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#34d3ee" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#34d3ee" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} opacity="0.5" />
      <path className="spark__line" d={line} />
      <circle className="spark__dot" cx={coords[coords.length - 1][0]} cy={coords[coords.length - 1][1]} r="2.6" />
    </svg>
  );
}

/* ------------------------------- 其它 ------------------------------- */

export function EmptyState({
  icon = 'box',
  title,
  desc,
  action,
}: {
  icon?: string;
  title: string;
  desc?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <span className="empty__ico">
        <Icon name={icon} size={22} />
      </span>
      <div className="empty__title">{title}</div>
      {desc && <div className="empty__desc">{desc}</div>}
      {action}
    </div>
  );
}

export function useCopy(): (text: string) => Promise<boolean> {
  return useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.ok('已复制到剪贴板');
      return true;
    } catch {
      toast.err('复制失败，请手动选择文本');
      return false;
    }
  }, []);
}
